/**
 * Build form draft strings from audit store, session overrides, and provenance.
 */
import type { UnitRef } from "@vera/rules";
import type { ViolationProvenance } from "@vera/shared";
import type { UserFactValue } from "../stores/userFactStore";
import type { MissingFactField } from "./missingFacts";
import type { UserFactRevision } from "./userFactAuditDb";

type ProvenanceFact = NonNullable<
  ViolationProvenance["userSupplied"]
>["facts"][number];

function provenanceToUserValue(value: ProvenanceFact["value"]): UserFactValue {
  if (
    value &&
    typeof value === "object" &&
    "value" in value &&
    "unit" in value
  ) {
    return {
      value: value.value,
      unit: value.unit as UnitRef,
    };
  }
  return value;
}

export function valueToDraftString(
  value: UserFactValue,
  field: MissingFactField
): string {
  if (field.kind === "boolean" && typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (field.kind === "enum" && typeof value === "string") return value;
  if (
    value &&
    typeof value === "object" &&
    "value" in value &&
    "unit" in value
  ) {
    return String(value.value);
  }
  return String(value);
}

/** Merge audit → overrides → provenance; pre-fill every active field. */
export function buildFactFormDraft(
  fields: MissingFactField[],
  sources: {
    audit?: UserFactRevision[];
    overrides?: Record<string, UserFactValue>;
    provenanceFacts?: ProvenanceFact[];
  }
): Record<string, string> {
  const values = new Map<string, UserFactValue>();

  for (const rev of sources.audit ?? []) {
    values.set(rev.factKey, rev.value);
  }
  for (const [key, value] of Object.entries(sources.overrides ?? {})) {
    values.set(key, value);
  }
  for (const pf of sources.provenanceFacts ?? []) {
    if (!values.has(pf.factKey)) {
      values.set(pf.factKey, provenanceToUserValue(pf.value));
    }
  }

  const draft: Record<string, string> = {};
  for (const field of fields) {
    const v = values.get(field.factKey);
    if (v === undefined) continue;
    draft[field.factKey] = valueToDraftString(v, field);
  }
  return draft;
}

export function formatUserSuppliedDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function latestUserSuppliedAt(
  facts: ProvenanceFact[] | undefined
): string | undefined {
  if (!facts?.length) return undefined;
  return facts.reduce(
    (latest, f) => (f.updatedAt > latest ? f.updatedAt : latest),
    facts[0]!.updatedAt
  );
}

export function userFactValuesEqual(
  a: UserFactValue | undefined,
  b: UserFactValue | undefined
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  if (typeof a === "boolean" || typeof a === "string") return a === b;
  if (
    typeof a === "object" &&
    a &&
    "value" in a &&
    typeof b === "object" &&
    b &&
    "value" in b
  ) {
    return a.value === b.value && a.unit === b.unit;
  }
  return false;
}
